/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { PasswordSchema } from '@therapy/shared-validators';
import { CheckCircle2, KeyRound, Loader2 } from 'lucide-react';
import { useCallback, useState, type SyntheticEvent, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { apiClient } from '../../lib/api/client.js';

const SetPasswordSchema = z
  .object({ newPassword: PasswordSchema, confirmPassword: z.string() })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
type SetPasswordInput = z.infer<typeof SetPasswordSchema>;

/**
 * Public activation / reset page: a parent (or any invited user) lands here from
 * their invite link, sets a password, and is sent to the unified login. Consumes
 * the single-use token via `POST /auth/reset`.
 */
export function SetPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SetPasswordInput>({ resolver: zodResolver(SetPasswordSchema) });

  const submit = useCallback(
    async (values: SetPasswordInput): Promise<void> => {
      setServerError(null);
      const body = { token, newPassword: values.newPassword };
      try {
        await apiClient.post('/auth/reset', body);
        setDone(true);
      } catch {
        // The token may belong to the staff surface — try that before failing.
        try {
          await apiClient.post('/auth/staff/reset', body);
          setDone(true);
        } catch {
          setServerError(t('setPassword.error'));
        }
      }
    },
    [token, t],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <span className="bg-brand-600 mb-5 flex h-12 w-12 items-center justify-center rounded-xl text-white">
          <KeyRound className="h-6 w-6" aria-hidden="true" />
        </span>

        {done ? (
          <div>
            <div className="mb-2 flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
              <h1 className="text-xl font-bold">{t('setPassword.doneTitle')}</h1>
            </div>
            <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
              {t('setPassword.doneBody')}
            </p>
            <Link to="/" className="block">
              <Button className="w-full">{t('setPassword.goToLogin')}</Button>
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              {t('setPassword.title')}
            </h1>
            <p className="mb-6 mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t('setPassword.subtitle')}
            </p>

            {token === '' ? (
              <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {t('setPassword.missingToken')}
              </p>
            ) : (
              <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
                <FormField
                  id="new-password"
                  type="password"
                  label={t('setPassword.newPassword')}
                  autoComplete="new-password"
                  error={errors.newPassword?.message}
                  registration={register('newPassword')}
                />
                <FormField
                  id="confirm-password"
                  type="password"
                  label={t('setPassword.confirmPassword')}
                  autoComplete="new-password"
                  error={errors.confirmPassword?.message}
                  registration={register('confirmPassword')}
                />
                {serverError !== null && (
                  <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                    {serverError}
                  </p>
                )}
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  {t('setPassword.submit')}
                </Button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
